import { useCallback, useEffect, useState } from "react"

import {
    Box,
    Grid,
    Paper,
    Typography,
    Chip,
    useTheme
} from "@mui/material"
import { Delete, Refresh, Toc } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"

import { Header } from "../../components/Header/Header"
import { ButtonBar } from "../../components/ButtonBar/ButtonBar"
import { PTable } from "../../components/PTable/PTable"
import type { ActionMenuItem } from "../../components/ActionMenu/ActionMenu"

import { strings } from "../../config/strings"

import type { PMPPPredictionJob, POrder } from "../../types/Types"

import { timeAgo } from "../../utils/stringsUtils"

import { numberOfRowsOptions, predictionJobsColumns } from "./default"
import { PDialog } from "../../components/PDialog/PDialog"
import { deletePredictionJob, getPredictionJobs } from "../../utils/api"



export const PredictionsListPage = () => {
    
    //State

    const [loading, setLoading] = useState<boolean>(false)
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false)

    const [currentPage, setCurrentPage] = useState<number>(0)
    const [currentTotalNumberOfItems, setCurrentTotalNumberOfItems] = useState<number>(0)
    const [currentNumberOfItemsPerPage, setCurrentNumberOfItemsPerPage] = useState<number>(numberOfRowsOptions[1])
    const [currentOrder, setCurrentOrder] = useState<POrder>('desc')
    const [currentOrderBy, setCurrentOrderBy] = useState<string>('creation_date')

    const [currentPredictionJobs, setCurrentPredictionJobs] = useState<PMPPPredictionJob[]>([]) 

    const [jobToBeDeleted, setJobToBeDeleted] = useState<PMPPPredictionJob | undefined>()

    //Hooks 

    const theme = useTheme()
    const navigate = useNavigate()


    //Callbacks

    const loadData = useCallback( async () => {
        try {
            setLoading(true)
        
            const predictionJobsRes = await getPredictionJobs(currentPage + 1, currentNumberOfItemsPerPage, currentOrderBy, currentOrder)
            
            setCurrentPredictionJobs(predictionJobsRes.items)
            setCurrentTotalNumberOfItems(predictionJobsRes.total)
        }
        catch(e: any) {
            console.log(e.data)
        }
        finally {
            setLoading(false)
        }

    }, [currentOrder, currentOrderBy, currentPage, currentNumberOfItemsPerPage])

   

    //Effects

    useEffect(() => {
        loadData()
    }, [currentOrder, currentOrderBy, currentPage, currentNumberOfItemsPerPage])



    //Handlers

    const handleJobDeletion = async () => {
        try {
            if(!jobToBeDeleted) 
                return
            setDeleteLoading(true)
            await deletePredictionJob(jobToBeDeleted.id)
        }
        catch (e){
            console.log(e)
        }
        finally {
            setJobToBeDeleted(undefined)
            setDeleteLoading(false)
            loadData()
        }
    }
    

    //Render

    const renderRows = () => {

        return currentPredictionJobs.map( (job) => {
        
            let stateChipColor = "primary"
            switch(job.state){
                case 'running':
                    stateChipColor = 'primary'
                    break
                case 'completed':
                    stateChipColor = 'success'
                    break
                case 'error':
                    stateChipColor = 'error'
                    break
            }

            let actions: ActionMenuItem[] = [{
                icon: <Delete/>,
                label: strings.action_delete,
                onClick: () => setJobToBeDeleted(job)
            }]

            if(job.state === 'completed'){
                actions.unshift({
                    icon: <Toc/>,
                    label: strings.action_show_results,
                    link: `./predictions/${job.id}`
                })
            }
            
            return {
                index: job.id,
                cells: [
                    <Typography sx={{wordBreak:'break-word'}}>{job.name}</Typography>,
                    <Chip label={job.state} variant={theme.palette.mode==='light'?'filled':'outlined'} color={stateChipColor as any} />,
                    <Typography sx={{wordBreak:'break-word'}}>{timeAgo(job.creation_date)}</Typography>,
                ],
                clickable: (job.state === 'completed'),
                selectable:  (job.state === 'completed'),
                ...(actions && {actions: actions})
            }
        })
    }

    return(
        <Box maxWidth='95%' margin='auto' py={2}>
            <Grid container spacing={2}>
                <Grid size={9}>
                    <Header 
                        title={strings.prediction_jobs} 
                        breadcrumbs={[]}
                    />
                </Grid>
                <Grid size={3}>
                    <ButtonBar buttons={[
                        {
                            label: strings.new_prediction,
                            link: 'new',
                            color: 'secondary'
                        },
                        {
                            icon: (<Refresh/>),
                            color: 'secondary',
                            onClick: ( () => loadData() )
                        }
                    ]}/>
                </Grid>
                <Grid size={12}>
                    <Paper sx={{overflow: 'hidden'}}>  
                        <PTable 
                            loading={loading}
                            showActions
                            columns={predictionJobsColumns} 
                            rows={renderRows()} 
                            page={currentPage} 
                            totalNumberOfRows={currentTotalNumberOfItems} 
                            numberOfRowsPerPage={currentNumberOfItemsPerPage}
                            rowsPerPageOptions={numberOfRowsOptions} 
                            onPageChange={ (page: number) => setCurrentPage(page) }
                            onRowsPerPageChange={ (rowsPerPage: number) => setCurrentNumberOfItemsPerPage(rowsPerPage) }
                            onSortChange={(orderBy, order) => {
                                setCurrentOrderBy(orderBy)
                                setCurrentOrder(order)
                            }}
                            onRowClick={ (rowIndex) => {navigate(`/predictions/${rowIndex}`)} }
                        />
                    </Paper>
                </Grid>
            </Grid>
            <PDialog 
                open={Boolean(jobToBeDeleted)} 
                onClose={() => setJobToBeDeleted(undefined)}
                title={strings.dialog_prediction_job_delete_title}
                body={strings.dialog_prediction_job_delete_body}
                onSubmit={handleJobDeletion}
                submitButtonLabel={strings.action_delete}
                submitButtonColor="error"
                loading={deleteLoading}
            />
        </Box>
    )
}